import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators';

@customElement('form-switch')
export class FormSwitch extends LitElement {
    @property({ type: String })
    name: string = '';

    @property({ type: Boolean })
    checked?: boolean;

    @property({ type: Boolean })
    colored: boolean = false;

    @property({ type: Boolean })
    disabled: boolean = false;

    @property({ type: Boolean })
    mini: boolean = false;

    private handleChangeChecked(e: Event) {
        this.checked = (e.target as HTMLInputElement).checked;
        this.dispatchEvent(
            new CustomEvent('change', {
                detail: this.checked,
            })
        );
    }

    setChecked(checked: boolean) {
        this.checked = checked;
        // force update by removing and adding the input element
        const input = this.shadowRoot?.querySelector('input');
        if (input) {
            const parent = input.parentElement;
            if (parent) {
                parent.removeChild(input);
                input.checked = checked;
                parent.appendChild(input);
            }
        }
    }

    protected render() {
        return html`
            <div class="form-check form-switch ${this.colored ? 'form-switch-colored' : ''} ${this.disabled ? 'form-switch-disabled' : ''} ${this.mini ? 'form-check-mini form-switch-mini' : 'form-check form-switch'}">
                <input
                    class="form-check-input ${this.mini ? 'form-check-input-mini' : ''}"
                    ?disabled="${this.disabled}"
                    id="${this.name}"
                    type="checkbox"
                    .checked="${this.checked ?? false}"
                    @change="${this.handleChangeChecked}"
                />
                <label class="form-check-label" for="${this.name}">
                    <slot></slot>
                </label>
            </div>
        `;
    }

    static get styles() {
        return css`
            .form-check {
                display: flex;
                min-height: 1.5rem;
                align-items: center;
                font-size: 0.75rem;
            }
            .form-check-input {
                vertical-align: top;
                background-repeat: no-repeat;
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
            }
            .form-check-input {
                width: 2rem;
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
                background-position: left center;
                border-radius: 2rem;
                transition: background-position 0.15s ease-in-out;
                height: 1.125rem;
                border: 0;
                background-color: var(--theme-checkbox-color);
                margin: 3px 0;
                min-width: 30px;
            }
            @media (prefers-reduced-motion: reduce) {
                .form-check-input {
                    transition: none;
                }
            }
            .form-check-input:focus {
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
            }
            .form-check-input:checked {
                background-position: right center;
            }
            .form-check-input:checked {
                background-color: var(--main-primary-color);
            }
            .form-check-label {
                color: var(--theme-input-color);
                margin-left: 0.25rem;
                flex: 1;
                font-size: 0.75rem;
            }
            .form-check-mini {
                font-size: 0.75rem;
                min-height: 1rem;
            }
            .form-switch-colored .form-check-input:checked {
                background-color: var(--main-success-color);
            }
            .form-switch-disabled .form-check-input:checked {
                background-color: var(--theme-checkbox-color);
            }
            .form-switch-mini {
                width: 1.5rem;
                height: 0.75rem;
                margin: 0;
            }

            .form-check-input-mini {
                width: 1.5rem;
                height: 0.75rem;
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
                background-position: left center;
                border-radius: 2rem;
                transition: background-position 0.15s ease-in-out;
                height: 0.9rem;
                border: 0;
                background-color: var(--theme-checkbox-color);
                margin: 3px 0;
                min-width: 25px;
            }
        `;
    }
}
